import { Container, Link, styled, Typography } from '@mui/joy'
import type { ContainerProps } from '@mui/joy/Container'
import Blob from '../../image/blob.svg'
import GetItOnGooglePlay from '../../image/get_it_on_google_play.png'
import './LandingPage.css'
import { ReactComponent as InstagramIcon } from '../../image/instagram_icon.svg'
import { ReactComponent as TwitterIcon } from '../../image/x_icon.svg'
import { ReactComponent as LinkedInIcon } from '../../image/linkedin_icon.svg'
import { ReactComponent as WhatsAppIcon } from '../../image/whatsapp_icon.svg'
import { useNavigate } from 'react-router-dom'
import Screenshot2 from '../../image/screenshot-2.png'
import Screenshot3  from '../../image/screenshot-3.png'
import Screenshot4 from '../../image/screenshot-4.png'
import Banner from '../../image/banner.png'
import React, {forwardRef, useEffect, useRef, useState} from 'react'

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.vars.palette.neutral[900],
    padding: '1rem',
    textDecoration: 'none',
    fontWeight: 'bold',
    '&:hover': {
        textDecoration: 'underline',
    },
}))

const FooterLink = styled(Link)(({ theme }) => ({
    color: theme.vars.palette.neutral[200],
    '&:hover': {
        textDecoration: 'underline',
    },
}))

const openPlayStore = () => {
    window.open(
        'https://play.google.com/store/apps/details?id=com.ezymoney.android',
        '_blank'
    )
}

export function LandingPage() {
    const heroRef = useRef<null | HTMLDivElement>(null)
    const featuresRef = useRef<null | HTMLDivElement>(null)
    const downloadRef = useRef<null | HTMLDivElement>(null)
    const footerRef = useRef<null | HTMLDivElement>(null)

    const scrollToSection = (ref:React.MutableRefObject<null | HTMLDivElement|undefined>)=>{
        ref.current?.scrollIntoView({ behavior: 'smooth' });
    }
    return (
        <div className="main-container">
            <NavigationBar 
                onHomeClick={()=>{scrollToSection(heroRef)}}
                onFeaturesClick={()=>{scrollToSection(featuresRef)}}
                onDownloadClick={()=>{scrollToSection(downloadRef)}}
                onContactUsClick={()=>{scrollToSection(footerRef)}}
            />

            <HeroSection />  
            <div style={{ display: 'block', textAlign: 'center' }} ref={featuresRef}>
                <Typography level="h2">Key Features</Typography>
                <AppScreenshots/>
            </div>
            <DownloadSection ref={downloadRef}/>
            <FooterSection ref={footerRef}/>
        </div>
    )
}
type HeroSectionProps = ContainerProps;

const HeroSection = forwardRef<HTMLDivElement, HeroSectionProps>((props,ref) =>{
    return (<div className="hero-container" ref={ref} {...props}>
        <div className="blob-container">
            <img src={Blob} alt="" className="blob-image" 
            onContextMenu={(event)=>{event.preventDefault()}}
            onDragStart={(event)=>{event.preventDefault()}}
             />
        </div>

        <div className="two-panel-row" style={{ flex: 1 }}>
            <div className="left-panel">
                <Typography level="h2">
                    Welcome to{' '}
                    <span style={{ color: '#FBBF24' }}>EzyMoney</span>{' '}
                    <br /> Your Smart Finance Companion
                </Typography>
                <Typography color="neutral" sx={{ py: '1.5rem', pr:'2rem' }}>
                    Managing your finances should be simple, intuitive,
                    and stress-free. With EzyMoney, we empower you to
                    take full control of your expenses, payments, and
                    budgeting, all in one place.
                </Typography>
                <img
                    src={GetItOnGooglePlay}
                    alt="Get it on Google play"
                    onClick={openPlayStore}
                    onContextMenu={(event)=>{event.preventDefault()}}
                    onDragStart={(event)=>{event.preventDefault()}}
                    style={{ width: 160, cursor: 'pointer' }}
                />
            </div>
            <div className="right-panel">
                <img
                    src={Banner}
                    alt="Hero"
                    onContextMenu={(event)=>{event.preventDefault()}}
                    onDragStart={(event)=>{event.preventDefault()}}
                />
            </div>
        </div>
    </div>);
})

interface NavigationBarProps {
    onHomeClick: () => void;
    onFeaturesClick: () => void;
    onDownloadClick: () => void;
    onContactUsClick: () => void;
}

function NavigationBar({onHomeClick,onFeaturesClick,onDownloadClick,onContactUsClick}:NavigationBarProps) {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
        setScrolled(window.scrollY > 0);
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
        window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return (
        <nav className={`nav-bar ${scrolled ? "scrolled" : ""}`}>
            <StyledLink onClick={onHomeClick}>Home</StyledLink>
            <StyledLink onClick={onFeaturesClick}>Features</StyledLink>
            <StyledLink onClick={onDownloadClick}>Download</StyledLink>
            <StyledLink onClick={onContactUsClick}>Contact Us</StyledLink>
        </nav>
    )
}

// function FeaturesCarousel() {
//     return (
//         <CarouselProvider
//             naturalSlideWidth={100}
//             naturalSlideHeight={125}
//             totalSlides={3}
//             lockOnWindowScroll={true}
//             infinite
//             interval={2000}
//         >
//             <Slider>
//                 <Slide index={0}>I am the first Slide.</Slide>
//                 <Slide index={1}>I am the second Slide.</Slide>
//                 <Slide index={2}>I am the third Slide.</Slide>
//             </Slider>
//         </CarouselProvider>
//     )
// }

function AppScreenshots() {
    return (
        <div className="screenshots-container">
            <div className="screenshot-item">
                <img src={Screenshot2} alt="Screenshot 1" className="screenshot" />
                <div className="feature-text">
                    <h3 className="feature-name">Transaction Overview</h3>
                    <p className="feature-description">Access a comprehensive view of your recent financial activities. Each transaction displays essential details, including bank information, date, time, and amount transmitted. Instantly see if funds were credited or debited, empowering you to stay effortlessly informed about your financial flow.</p>
                </div>
            </div>

            <div className="screenshot-item ">
                <div className="feature-text">
                    <h3 className="feature-name">Financial Insights</h3>
                    <p className="feature-description">Visualize your spending and income patterns with dynamic graphs and pie charts. Gain clear insights into different categories and track your financial health over customizable timeframes. Tailor reports to suit your needs, empowering smarter financial decisions with ease.</p>
                </div>
                <img src={Screenshot3} alt="Screenshot 2" className="screenshot" />
            </div>

            <div className="screenshot-item">
                <img src={Screenshot4} alt="Screenshot 3" className="screenshot" />
                <div className="feature-text">
                    <h3 className="feature-name">Detailed Transaction Breakdown</h3>
                    <p className="feature-description">Dive deep into each transaction with complete details, including account name, payment method, category, and reference number. Effortlessly share transaction records when needed, ensuring clear and organized tracking of your financial activities.</p>
                </div>
            </div>
        </div>
    );
}

type DownloadSectionProps = ContainerProps; // Define props if there are any (currently empty)

const DownloadSection = forwardRef<HTMLDivElement, DownloadSectionProps>((props, ref) => {
    return (
        <Container
            maxWidth="md"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pb: '8rem',
                pt: '4rem',
            }}
            role="section"
            {...props}
            ref={ref}
        >
            <Typography level="h3">Get Started Today!</Typography>
            <Typography sx={{ textAlign: 'center', py: '2rem' }}>
                Join our growing community of users who are mastering their
                finances with ease. Download the app on the Play Store and start
                your journey to better financial health.
            </Typography>
            <img
                src={GetItOnGooglePlay}
                alt="Get it on Google play"
                onClick={openPlayStore}
                style={{ width: 160, cursor: 'pointer' }}
            />
        </Container>
    )
})

/* function ContactUsSection() {
   
} */

type FooterSectionProps = ContainerProps; // Define props if there are any (currently empty)

const FooterSection = forwardRef<HTMLDivElement,FooterSectionProps>((props,ref)=>{
    const navigate = useNavigate()

    return (
        <footer className="footer" ref={ref} {...props}>
            <div className="footer-content">
                <div className="footer-column">
                    <Typography
                        level="h4"
                        className="footer-title"
                        textColor="common.white"
                        fontWeight={600}
                    >
                        Quick Links
                    </Typography>
                    <div className="footer-links">
                        <FooterLink
                            onClick={() => {
                                navigate('terms-of-service')
                            }}
                        >
                            Terms of Service
                        </FooterLink>
                        <FooterLink
                            onClick={() => {
                                navigate('privacy-policy')
                            }}
                        >
                            Privacy Policy
                        </FooterLink>
                        <FooterLink>Help & FAQ</FooterLink>
                        <FooterLink href="https://forms.gle/WrPBTYnun5dKc7sm6" target="_blank" rel="noopener noreferrer">
                        Request a Feature</FooterLink>
                    </div>
                </div>
                <div className="footer-column">
                    <Typography
                        level="h4"
                        className="footer-title"
                        textColor={'common.white'}
                    >
                        Contact Us
                    </Typography>
                    <div className="footer-contact">
                        <Typography textColor={'common.white'}>
                            Sachin Kumar
                        </Typography>
                        <Typography textColor={'common.white'}>
                            <FooterLink href="mailto:support@ezymoney.in">
                                support@ezymoney.in
                            </FooterLink>
                        </Typography>
                        <Typography textColor={'common.white'}>
                            <FooterLink href="tel:+918864078997">+91-8864078997</FooterLink>
                        </Typography>
                    </div>
                </div>
                <div className="footer-column">
                    <Typography
                        level="h4"
                        className="footer-title"
                        textColor={'common.white'}
                    >
                        Follow Us
                    </Typography>

                    <div className="footer-social-icons">
                        <LinkedInIcon className="social-icon" onClick={()=>{
                            window.open('https://www.linkedin.com/showcase/ezymoney/')
                        }}/>
                        <WhatsAppIcon className="social-icon" onClick={()=>{
                            window.open('https://wa.link/tzzsne')
                        }} />
                        <TwitterIcon className="social-icon" onClick={()=>{
                            window.open('https://x.com/info_ezymoney')
                        }} />
                        <InstagramIcon className="social-icon" onClick={()=>{
                            window.open('https://www.instagram.com/ezymoney.in/')
                        }} />
                    </div>
                </div>
            </div>
            <small className="footer-copy">
                © 2024 EzyMoney. All rights reserved.
            </small>
        </footer>
    )
})
